import * as React from 'react';
import { connect } from 'redux-scaffolding-ts';
import { LocationDto, DropDownLocationsStore, LocationsStore } from '../../stores/configuration/locations/locations-store';
import { nameof } from '../../utils/object';
import { ItemReference } from 'stores/dataStore';
import { isNullOrWhiteSpaces } from 'utils/useful-functions';
import { IdentityService } from 'services/identity-service';
import { container } from 'inversify.config';
import { useCallback, useEffect, useRef, useState } from 'react';
import TailwindSelect from 'widgets/editors/tailwind-select';
import { useTranslation } from 'react-i18next';

export interface LocationItemReference extends ItemReference {
  countryName?: string;
  countryId?: string;
}

interface TailwindLocationEditorProps {
  value: LocationItemReference | ItemReference;
  onChange?: (value: LocationItemReference | ItemReference) => void;
  locations?: LocationsStore;
  placeholder?: string;
  clearable?: boolean;
  nullable?: boolean;
  readOnly?: boolean;
  className?: string;
  locationsReceived?: string[];
  filterLocationsByActiveRole?: boolean;
  showCountry?: boolean;
  loadDataOnOpen?: boolean;
  onBlur?: (event: React.FocusEvent) => void;
  onFocus?: (event: React.FocusEvent) => void;
}

const TailwindLocationEditor: React.FC<TailwindLocationEditorProps> = ({
  value,
  onChange,
  locations,
  placeholder,
  clearable = true,
  readOnly = false,
  className = '',
  nullable = false,
  locationsReceived,
  filterLocationsByActiveRole,
  showCountry,
  loadDataOnOpen = false,
  onBlur,
  onFocus
}) => {
  const identityService: IdentityService = container.get(IdentityService);
  const { t } = useTranslation();
  const [selectedValue, setSelectedValue] = useState<ItemReference>(value);
  const [options, setOptions] = useState<{ value: string; label: string }[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const isMounted = useRef(true);

  const loadLocationMethods = useCallback(
    async (search: string = '') => {
      if (!isMounted.current) return;
      try {
        setIsLoading(true);
        let filter;
        const filterActiveAndStartswith = isNullOrWhiteSpaces(search)
          ? `active eq true`
          : `active eq true and startswith(tolower(${nameof<LocationDto>('location')}), '${search.toLowerCase()}')`;

        if (locationsReceived && locationsReceived.length > 0) {
          filter = [{ id: { in: { type: 'guid', value: locationsReceived } } }, filterActiveAndStartswith];
        } else if (filterLocationsByActiveRole) {
          let locations = identityService.getCurrentRoleLocations() || [];
          if (!locations || locations.length > 0) filter = [{ id: { in: { type: 'guid', value: locations } } }, filterActiveAndStartswith];
          else filter = [filterActiveAndStartswith];
        } else {
          filter = [filterActiveAndStartswith];
        }

        const result = await locations.getAllAsync({
          searchQuery: '',
          skip: 0,
          take: 100000,
          orderBy: [{ direction: 'Ascending', field: nameof<LocationDto>('location'), useProfile: false }],
          filter: filter
        });
        if (isMounted.current && result) {
          setOptions(
            result.items.map(item => ({
              value: item.id,
              label: item.location
            }))
          );
        }
      } catch (error) {
        if (isMounted.current) {
          console.error('Error loading priorities:', error);
          setOptions([]);
        }
      } finally {
        if (isMounted.current) {
          setIsLoading(false);
        }
      }
    },
    [locations, filterLocationsByActiveRole, identityService, locationsReceived]
  );

  const onOpenDropDown = useCallback(() => {
    if (loadDataOnOpen && options.length === 0) {
      loadLocationMethods();
    }
  }, [loadDataOnOpen, options.length, loadLocationMethods]);

  useEffect(() => {
    isMounted.current = true;
    if (!loadDataOnOpen || (value && options.length === 0)) {
      loadLocationMethods();
    }

    return () => {
      isMounted.current = false;
    };
  }, [loadDataOnOpen, value, options.length, loadLocationMethods]);

  useEffect(() => {
    if (isMounted.current) {
      setSelectedValue(value);
    }
  }, [value]);

  const handleChange = (newValue: string | null) => {
    if (!isMounted.current) return;
    const selected = newValue ? options.find(opt => opt.value === newValue) : null;
    const itemRef = selected ? { id: selected.value, title: selected.label } : null;
    setSelectedValue(itemRef);
    onChange?.(itemRef);
  };

  return (
    <TailwindSelect
      options={
        isLoading
          ? [{ id: 'loading', text: t('Loading...'), disabled: true }]
          : options.length > 0
          ? options.map(delivery => ({
              id: delivery.value,
              text: delivery.label
            }))
          : [{ id: 'no-options', text: t('No options available'), disabled: true }]
      }
      value={selectedValue?.id || ''}
      onChange={handleChange}
      placeholder={placeholder || 'Select an option'}
      clearable
      searchable={!isLoading}
      disabled={readOnly}
      className="w-full"
      onOpenDropDown={onOpenDropDown}
    />
  );
};

// Wire up the React component to the Redux store
export default connect(['locations', DropDownLocationsStore])(TailwindLocationEditor);
